import { AssigneeType, PostStatusPropertyValue } from 'api/clients/PropertyClient'
import { Button } from 'blaze-ui/Button'
import { AI, Copy, Edit1, Grid } from 'blaze-ui/icons'
import {
  ArticlePostPreviewActionTypes,
  useArticlePostPreviewContext,
} from 'components/ArticlePostPreview/ArticlePostPreviewContext'
import { usePostProperties } from 'components/ArticlePostPreview/usePostProperties'
import { platformKeyByChannel, SocialChannelType } from 'common/ShareSheetV2/Screens/Default/Default'
import { UserIntent } from 'components/PostStatusDropdown/Types'
import Styles from './Actions.module.scss'
import ArrowSwitchHorizontal from 'blaze-ui/icons/20/ArrowSwitchHorizontal'
import { CrossPostDrawer } from '../CrossPostDrawer/CrossPostDrawer'
import { PostButton } from './PostButton'
import { ButtonLink } from 'blaze-ui/Button/ButtonLink'
import { ChannelIcon } from 'components/ChannelIcon/ChannelIcon'
import { useRootStore } from 'stores/RootStore'
import { observer } from 'mobx-react-lite'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { TurnIntoWizard } from 'components/Wizards/TurnInto/TurnIntoWizard'
import { useModals } from 'almanac-ui/Modal'
import { Can } from 'components/Can'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useAuthedArticle } from 'components/ArticlePostPreview/hooks'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useInformalFeedbackRequests } from 'hooks/almanacFiles/useInformalFeedbackRequests'
import { useAuth } from 'hooks/UseAuth'
import { usePaywallGuard } from 'hooks/usePaywallGuard'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'

export const Actions = observer(() => {
  const { data } = useExternalIntegrations()
  const integrations = data?.external_integrations
  const { openModal } = useModals()
  const {
    state: { content, loading, channel },
    validationErrors,
    almanacFile,
    dispatch,
    scheduledPost,
  } = useArticlePostPreviewContext()
  const { drawersStore } = useRootStore()
  const notPostable = validationErrors.length > 0
  const { status } = usePostProperties({
    id: almanacFile.id,
    type: AssigneeType.Article,
  })
  const { isMobile } = useResponsiveDesign()
  const { openRequest } = useInformalFeedbackRequests(almanacFile.id)
  const auth = useAuth()

  const authedArticle = useAuthedArticle(almanacFile)
  const currentWorkspaceId = useCurrentWorkspaceId()
  const workspace = useCurrentWorkspace()

  const isAutopilotUser = workspace?.is_virtual_marketer

  const hasIntegrationForChannel = integrations?.some(
    (integration) => integration.provider === platformKeyByChannel[almanacFile.channel as SocialChannelType],
  )

  const guardedHandleCrossPost = usePaywallGuard(() => drawersStore.openDrawer(CrossPostDrawer))
  const guardedTurnInto = usePaywallGuard(() => {
    openModal(TurnIntoWizard, {
      fromDocProps: {
        documentAsMarkdown: content,
      },
    })
  })

  if (!auth) return null

  return (
    <div className={Styles.container}>
      <Can I="edit" a={authedArticle} inWorkspace={currentWorkspaceId}>
        <PostButton
          channel={almanacFile.channel as SocialChannelType}
          disabledKeys={notPostable ? [UserIntent.ScheduleAutoPost, UserIntent.PostNow] : []}
          hasIntegrationForChannel={hasIntegrationForChannel}
          afterExecute={(intent) => {
            if (intent === UserIntent.ScheduleAutoPost) {
              dispatch({ type: ArticlePostPreviewActionTypes.SET_EDITING, payload: false })
            }
          }}
          variant={!!openRequest ? 'tertiary' : 'primary'}
          onAction={(action) => {
            if (action === UserIntent.EditContent) {
              dispatch({ type: ArticlePostPreviewActionTypes.SET_EDITING, payload: true })
            }
          }}
          size={isMobile ? 'xl' : 'lg'}
          assignee={{ id: almanacFile.id, type: AssigneeType.Article }}
        />
      </Can>
      <div className={Styles.buttons}>
        <Can I="edit" a={authedArticle} inWorkspace={currentWorkspaceId}>
          <Button
            frontIcon={Edit1}
            fullWidth
            variant="tertiary"
            isDisabled={loading || scheduledPost?.is_posting}
            onPress={() => {
              // Need to add a timeout so that the click event doesn't bleed through to the buttons on the next screen
              setTimeout(() => dispatch({ type: ArticlePostPreviewActionTypes.SET_EDITING, payload: true }), 50)
            }}
          >
            Edit Content
          </Button>
        </Can>
        {status?.value === PostStatusPropertyValue.Posted && scheduledPost?.post_url && (
          <ButtonLink fullWidth variant="tertiary" isDisabled={loading} to={scheduledPost.post_url}>
            <div className={Styles.buttonLink}>
              <ChannelIcon channel={channel} size={20} />
              See Post
            </div>
          </ButtonLink>
        )}
        {(scheduledPost?.alternative_options || []).length > 0 || loading ? (
          <Can I="edit" a={authedArticle} inWorkspace={currentWorkspaceId}>
            <Button
              frontIcon={AI}
              fullWidth
              variant="tertiary"
              isDisabled={loading || scheduledPost?.is_posting}
              onPress={() => {
                // Need to add a timeout so that the click event doesn't bleed through to the buttons on the next screen
                setTimeout(() => {
                  dispatch({ type: ArticlePostPreviewActionTypes.SET_CHANGING_COPY, payload: true })
                  dispatch({ type: ArticlePostPreviewActionTypes.SET_OLD_COPY, payload: content })
                }, 50)
              }}
            >
              Copy Options
            </Button>
          </Can>
        ) : (
          <Button
            frontIcon={Copy}
            fullWidth
            variant="tertiary"
            isDisabled={loading || scheduledPost?.is_posting}
            onPress={() => {
              if (!scheduledPost?.content) return

              navigator.clipboard.writeText(scheduledPost.content)
              showToastMessage('Post text copied!', 'success')
            }}
          >
            Copy Text
          </Button>
        )}
        {(status?.value !== PostStatusPropertyValue.Posted || scheduledPost?.is_posting) && !isAutopilotUser && (
          <Button
            frontIcon={ArrowSwitchHorizontal}
            fullWidth
            variant="tertiary"
            isDisabled={loading}
            onPress={guardedHandleCrossPost}
          >
            Cross Post
          </Button>
        )}
        <Button frontIcon={Grid} fullWidth variant="tertiary" isDisabled={loading} onPress={guardedTurnInto}>
          Turn Into
        </Button>
      </div>
    </div>
  )
})
